import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Snackbar,
  Stack,
  Switch,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  CheckCircle,
  Chat,
  ContentCopy,
  ErrorOutline,
  HelpOutline,
  LinkOff,
  Refresh,
  Save,
  Science,
  Search,
  Sync,
} from '@mui/icons-material';
import StudioSelector, { StudioOption } from '../components/StudioSelector';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/apiService';
import { colors } from '../theme/theme';
import { STUDIO_SCOPED_ROLES } from '../utils/roles';
import BranchSelect from '../components/BranchSelect';
import { useMyLocations } from '../hooks/useMyLocations';
import CreateTemplateDialog from '../components/whatsapp/CreateTemplateDialog';

/**
 * MESSAGING — WhatsApp / push / email delivery console.
 *
 * Each studio connects ITS OWN Twilio account (Account SID + Auth Token), its
 * own WhatsApp sender (Messaging Service) and its own approved templates; every
 * send, delivery receipt and template approval runs on that account. Gated on
 * studioPermissions.enableMessaging (enforced by the backend too); super admins
 * pick the studio and set the flag.
 */

const STATUS_COLOR: Record<string, any> = {
  delivered: 'success',
  read: 'success',
  sent: 'info',
  queued: 'default',
  pending: 'default',
  failed: 'error',
  suppressed: 'warning',
  expired: 'warning',
  cancelled: 'default',
};

const TEMPLATE_STATUS_HINT: Record<string, string> = {
  approved: '',
  draft: 'created, not submitted to WhatsApp yet',
  submitted: 'awaiting WhatsApp review',
  rejected: 'rejected by WhatsApp',
  paused: 'paused by WhatsApp for low quality',
  disabled: 'disabled by WhatsApp',
  not_registered: 'not set up for this studio',
};

const EMPTY_WA = {
  enabled: false,
  messagingServiceSid: '',
  fromNumber: '',
  senderName: '',
  quietHours: '',
};
const EMPTY_CREDS = { accountSid: '', authToken: '', apiKey: '', apiSecret: '' };

/** Only send fields that were filled in — '' must never overwrite a credential. */
const filled = (obj: Record<string, string>) =>
  Object.fromEntries(
    Object.entries(obj)
      .map(([k, v]) => [k, (v ?? '').trim()])
      .filter(([, v]) => v),
  );

const errMsg = (e: any, fallback: string) =>
  e?.message || e?.data?.message || fallback;

/**
 * When embedded in Engagement → WhatsApp, the hub owns the header and the
 * workspace/studio picker; this page only renders the requested tool tabs.
 */
export interface MessagingEmbed {
  studioId: string;
  isPlatform: boolean;
  /** Which tool tabs to show (0 Overview … 6 Replies). */
  tabs: number[];
}

const TAB_LABELS = [
  'Overview',
  'Send message',
  'WhatsApp setup',
  'Templates',
  'Delivery log',
  'Member lookup',
  'Replies',
];

const MessagingPage: React.FC<{ embedded?: MessagingEmbed }> = ({ embedded }) => {
  const { admin } = useAuth();
  const role = admin?.role ?? '';
  const isStudioRole = STUDIO_SCOPED_ROLES.includes(role);
  // Instructors may look but not change anything (the backend refuses too).
  const canWrite = role !== 'instructor';
  // Under "All locations" a studio role picks ONE location first — each has its
  // own messaging setup — and every read and write is pinned to it.
  const { isAllLocations, byId } = useMyLocations();
  const [location, setLocation] = useState<string>('');
  const mustPickLocation = !embedded && isStudioRole && isAllLocations;
  const pickedLocation = mustPickLocation && location ? byId(location) : undefined;
  const messagingEnabled =
    (pickedLocation?.studioPermissions
      ? pickedLocation.studioPermissions.enableMessaging
      : (admin?.studio as any)?.studioPermissions?.enableMessaging) === true;
  const blocked = isStudioRole && !messagingEnabled;

  const [studio, setStudio] = useState<StudioOption | null>(null);
  // Super admin: SweatSocial's OWN WhatsApp number (what SweatSocial-app
  // members hear from) vs a studio's number (what its whitelabel-app members hear from).
  const [workspace, setWorkspace] = useState<'studio' | 'platform'>('studio');
  // SweatSocial's own number is a super-admin-only surface (backend enforces it too).
  const isSuperAdmin = role === 'super_admin';
  const isPlatform = embedded ? embedded.isPlatform : isSuperAdmin && workspace === 'platform';
  const studioId = embedded
    ? embedded.studioId
    : isStudioRole
      ? mustPickLocation
        ? location || undefined
        : (admin?.studio as any)?._id
      : isPlatform
        ? 'platform'
        : studio?._id;

  const visibleTabs = embedded?.tabs ?? [0, 1, 2, 3, 4, 5, 6];
  const [tab, setTab] = useState(visibleTabs[0]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);

  const [overview, setOverview] = useState<any>(null);
  const [channels, setChannels] = useState<any[]>([]);
  const [templates, setTemplates] = useState<any>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [replies, setReplies] = useState<any[] | null>(null);
  const [replyTo, setReplyTo] = useState<any>(null);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);
  const [lookupId, setLookupId] = useState('');
  const [lookupRows, setLookupRows] = useState<any[] | null>(null);
  const [consent, setConsent] = useState<any>(null);

  const [waForm, setWaForm] = useState<any>(EMPTY_WA);
  const [credForm, setCredForm] = useState<any>(EMPTY_CREDS);
  const [showApiKey, setShowApiKey] = useState(false);
  const [savingCreds, setSavingCreds] = useState(false);
  const [connection, setConnection] = useState<any>(null);
  const [checking, setChecking] = useState(false);

  const [testOpen, setTestOpen] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testMode, setTestMode] = useState<'direct' | 'pipeline'>('direct');
  const [testTemplate, setTestTemplate] = useState('');
  const [testBody, setTestBody] = useState('');
  const [testVars, setTestVars] = useState<Record<string, string>>({});
  const [testUserId, setTestUserId] = useState('');
  const [testResult, setTestResult] = useState<any>(null);
  const [testing, setTesting] = useState(false);

  // Template setup dialog
  const [tplDialog, setTplDialog] = useState<any>(null); // the row being set up
  const [providerTpls, setProviderTpls] = useState<any[] | null>(null);
  const [providerErr, setProviderErr] = useState<string | null>(null);
  const [tplSid, setTplSid] = useState('');
  const [tplVars, setTplVars] = useState<string[]>([]);
  const [savingTpl, setSavingTpl] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // Send-to-member
  const [memberQuery, setMemberQuery] = useState('');
  const [memberResults, setMemberResults] = useState<any[]>([]);
  const [selectedMembers, setSelectedMembers] = useState<any[]>([]);
  const [audience, setAudience] = useState<'pick' | 'all'>('pick');
  const [memberSummary, setMemberSummary] = useState<any>(null);
  const [sendTemplate, setSendTemplate] = useState('');
  const [sendVars, setSendVars] = useState<Record<string, string>>({});
  const [sending, setSending] = useState(false);

  const checkConnection = useCallback(async () => {
    if (!studioId) return;
    setChecking(true);
    try {
      const res: any = await apiService.verifyMessagingChannel('whatsapp', studioId);
      setConnection(res?.data ?? res);
    } catch (e: any) {
      setConnection({ error: errMsg(e, 'Could not check the connection') });
    } finally {
      setChecking(false);
    }
  }, [studioId]);

  const [createOpen, setCreateOpen] = useState(false);
  const load = useCallback(async () => {
    if (!studioId) return;
    setLoading(true);
    try {
      const [ov, ch, tpl, msg] = await Promise.all([
        apiService.getMessagingOverview(studioId),
        apiService.getMessagingChannels(studioId),
        apiService.getMessagingTemplates(studioId),
        apiService.getMessages({ limit: 50 }, studioId),
      ]);
      setOverview((ov as any)?.data ?? ov);
      const chList = ((ch as any)?.data ?? ch)?.configs ?? [];
      setChannels(chList);
      setTemplates((tpl as any)?.data ?? tpl);
      setMessages(((msg as any)?.data ?? msg)?.messages ?? []);

      // Always reset: a studio with no WhatsApp config must not inherit the
      // previously selected studio's sender (Save would copy it across).
      const wa = chList.find((c: any) => c.channel === 'whatsapp');
      setWaForm(
        wa
          ? {
              enabled: !!wa.enabled,
              messagingServiceSid: wa.messagingServiceSid ?? '',
              fromNumber: wa.fromNumber ?? '',
              senderName: wa.senderName ?? '',
              quietHours: wa.quietHours ?? '',
            }
          : EMPTY_WA,
      );
    } catch (e: any) {
      setToast({ msg: errMsg(e, 'Failed to load messaging data'), sev: 'error' });
    } finally {
      setLoading(false);
    }
  }, [studioId]);

  // New studio → clear everything that belonged to the previous one.
  useEffect(() => {
    setConnection(null);
    setCredForm(EMPTY_CREDS);
    setSelectedMembers([]);
    setAudience('pick');
    setMemberSummary(null);
    setSendTemplate('');
    setSendVars({});
    setLookupId('');
    setLookupRows(null);
    setConsent(null);
    setTestResult(null);
    setTestTemplate('');
    setTestVars({});
    setTestBody('');
    setTestTo('');
    setTestUserId('');
    setTplDialog(null);
    setProviderTpls(null);
    setReplies(null);
    setReplyTo(null);
    load();
  }, [load]);

  const loadReplies = useCallback(async () => {
    if (!studioId) return;
    try {
      const res: any = await apiService.getMessagingInbound({ limit: 100 }, studioId);
      setReplies((res?.data ?? res)?.replies ?? []);
    } catch (e: any) {
      setToast({ msg: errMsg(e, 'Could not load replies'), sev: 'error' });
      setReplies([]);
    }
  }, [studioId]);

  useEffect(() => {
    if (tab === 6 && replies === null) loadReplies();
  }, [tab, replies, loadReplies]);

  const sendReply = async () => {
    if (!replyTo || !replyText.trim()) return;
    setReplying(true);
    try {
      await apiService.replyToInbound(replyTo._id, replyText.trim(), studioId);
      setToast({ msg: 'Reply sent — see the Delivery log for delivery', sev: 'success' });
      setReplyTo(null);
      setReplyText('');
      load();
    } catch (e: any) {
      setToast({ msg: errMsg(e, 'Reply failed'), sev: 'error' });
    } finally {
      setReplying(false);
    }
  };

  useEffect(() => {
    if (tab === 2 && studioId && !connection) checkConnection();
  }, [tab, studioId, connection, checkConnection]);

  const waConfig = channels.find((c: any) => c.channel === 'whatsapp');

  const saveChannel = async () => {
    try {
      await apiService.updateMessagingChannel({ channel: 'whatsapp', ...waForm }, studioId);
      setToast({ msg: 'WhatsApp sender saved', sev: 'success' });
      await load();
      checkConnection();
    } catch (e: any) {
      setToast({ msg: errMsg(e, 'Save failed'), sev: 'error' });
    }
  };

  const saveCredentials = async () => {
    setSavingCreds(true);
    try {
      const res: any = await apiService.setMessagingCredentials(
        { channel: 'whatsapp', ...filled(credForm) },
        studioId,
      );
      const data = res?.data ?? res;
      const name = data?.account?.friendlyName;
      setCredForm(EMPTY_CREDS);
      setToast(
        data?.warning
          ? { msg: data.warning, sev: 'error' }
          : {
              msg: name ? `Connected to Twilio account “${name}”` : 'Twilio account connected',
              sev: 'success',
            },
      );
      await load();
      checkConnection();
    } catch (e: any) {
      setToast({ msg: errMsg(e, 'Could not save credentials'), sev: 'error' });
    } finally {
      setSavingCreds(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect this Twilio account? WhatsApp will be switched off for this studio.')) return;
    try {
      await apiService.disconnectMessagingChannel('whatsapp', studioId);
      setToast({ msg: 'Twilio account disconnected', sev: 'success' });
      await load();
      checkConnection();
    } catch (e: any) {
      setToast({ msg: errMsg(e, 'Disconnect failed'), sev: 'error' });
    }
  };

  const copy = (text?: string) => {
    if (!text) return;
    navigator.clipboard
      .writeText(text)
      .then(() => setToast({ msg: 'Copied', sev: 'success' }))
      .catch(() => setToast({ msg: 'Copy failed — select the URL and copy it manually', sev: 'error' }));
  };

  const testTemplateRow = (templates?.templates ?? []).find(
    (t: any) => t.templateKey === testTemplate,
  );

  const runTestSend = async () => {
    setTestResult(null);
    setTesting(true);
    try {
      if (testMode === 'pipeline') {
        const res: any = await apiService.messagingTestPipeline(
          { userId: testUserId.trim(), channel: 'whatsapp' },
          studioId,
        );
        const data = res?.data ?? res;
        setTestResult({ ok: true, detail: data?.note, correlationId: data?.correlationId });
        setToast({ msg: 'Emitted — check the Delivery Log', sev: 'success' });
        setTimeout(load, 4000);
        return;
      }

      const res: any = await apiService.messagingTestSend(
        {
          channel: 'whatsapp',
          to: testTo.trim(),
          ...(testTemplate
            ? { templateKey: testTemplate, params: testVars }
            : testBody.trim()
              ? { body: testBody.trim() }
              : {}),
        },
        studioId,
      );
      const data = res?.data ?? res;
      setTestResult(
        data?.ok
          ? {
              ok: true,
              detail: `${data.note ?? 'Accepted by WhatsApp.'} (status: ${data.providerStatus ?? data.status}, ${data.providerMessageId})`,
            }
          : {
              ok: false,
              detail:
                (data?.failure?.humanReason ?? 'Send failed') +
                (data?.failure?.providerCode ? ` (error ${data.failure.providerCode})` : ''),
            },
      );
    } catch (e: any) {
      setTestResult({ ok: false, detail: errMsg(e, 'Test failed') });
    } finally {
      setTesting(false);
    }
  };

  // ── Templates ────────────────────────────────────────────────────────────
  const loadProviderTemplates = async () => {
    setProviderTpls(null);
    setProviderErr(null);
    try {
      const res: any = await apiService.getProviderTemplates(studioId);
      setProviderTpls((res?.data ?? res)?.templates ?? []);
    } catch (e: any) {
      setProviderErr(errMsg(e, 'Could not load templates from Twilio'));
      setProviderTpls([]);
    }
  };

  const openTemplateSetup = async (row: any) => {
    setTplDialog(row);
    setTplSid(row.providerTemplateId ?? '');
    setTplVars(row.variableOrder?.length ? row.variableOrder : []);
    if (!providerTpls) loadProviderTemplates();
  };

  // An approved template's mapping is frozen unless it points at a new Twilio template.
  const mappingLocked =
    tplDialog?.status === 'approved' && tplSid === tplDialog?.providerTemplateId;

  const chosenProviderTpl = (providerTpls ?? []).find((p: any) => p.sid === tplSid);

  // Keep one mapping field per {{n}} in the chosen Twilio template.
  useEffect(() => {
    if (!tplDialog || !chosenProviderTpl) return;
    const n = chosenProviderTpl.variableCount ?? 0;
    setTplVars((prev) =>
      Array.from({ length: n }, (_, i) => prev[i] ?? tplDialog.suggestedVariables?.[i] ?? ''),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tplSid, chosenProviderTpl?.variableCount]);

  const saveTemplate = async () => {
    if (!tplDialog || !tplSid) return;
    setSavingTpl(true);
    try {
      await apiService.upsertMessagingTemplate(
        {
          templateKey: tplDialog.templateKey,
          providerTemplateId: tplSid,
          variableOrder: tplVars.map((v) => v.trim()),
        },
        studioId,
      );
      setToast({ msg: `${tplDialog.templateKey} linked`, sev: 'success' });
      setTplDialog(null);
      load();
    } catch (e: any) {
      setToast({ msg: errMsg(e, 'Could not save the template'), sev: 'error' });
    } finally {
      setSavingTpl(false);
    }
  };

  const syncTemplates = async () => {
    setSyncing(true);
    try {
      const res: any = await apiService.syncMessagingTemplates(studioId);
      const data = res?.data ?? res;
      setToast({ msg: `Refreshed ${data?.synced ?? 0} template(s) from Twilio`, sev: 'success' });
      load();
    } catch (e: any) {
      setToast({ msg: errMsg(e, 'Sync failed'), sev: 'error' });
    } finally {
      setSyncing(false);
    }
  };

  const submitTemplate = async (row: any) => {
    try {
      // Campaign templates carry the category they were created with.
      await apiService.submitMessagingTemplate(row.templateKey, row.category ?? 'UTILITY', studioId);
      setToast({ msg: `${row.templateKey} sent to WhatsApp for approval`, sev: 'success' });
      load();
    } catch (e: any) {
      setToast({ msg: errMsg(e, 'Submission failed'), sev: 'error' });
    }
  };

  const removeTemplate = async (row: any) => {
    if (!row._id || !window.confirm(`Unlink ${row.templateKey}?`)) return;
    try {
      await apiService.deleteMessagingTemplate(row._id, studioId);
      setToast({ msg: `${row.templateKey} unlinked`, sev: 'success' });
      load();
    } catch (e: any) {
      setToast({ msg: errMsg(e, 'Could not unlink'), sev: 'error' });
    }
  };

  const searchMembers = useCallback(async (q: string) => {
    if (!studioId) return;
    try {
      const res: any = await apiService.searchMessagingMembers(q, studioId);
      setMemberResults(((res as any)?.data ?? res)?.members ?? []);
    } catch {
      setMemberResults([]);
    }
  }, [studioId]);

  // Debounced so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => { if (tab === 1) searchMembers(memberQuery); }, 300);
    return () => clearTimeout(t);
  }, [memberQuery, tab, searchMembers]);

  const activeTemplate = (templates?.templates ?? []).find(
    (t: any) => t.templateKey === sendTemplate,
  );

  // Left blank, these are filled in per member by the backend. SweatSocial is
  // the marketplace, not a studio — in its workspace the studio must be named.
  const AUTO_VARS: Record<string, string> = isPlatform
    ? { userName: "each user's name" }
    : { userName: "each member's name", studioName: 'your studio name' };

  const previewText = () => {
    const body = activeTemplate?.bodyPreview;
    if (!body) return null;
    const sample: Record<string, string> = {
      userName: selectedMembers[0]?.name ?? 'Asha',
      ...(isPlatform
        ? {}
        : { studioName: (admin?.studio as any)?.studioName ?? studio?.studioName ?? 'your studio' }),
    };
    return (activeTemplate?.variableOrder ?? []).reduce(
      (acc: string, name: string, i: number) =>
        acc.replace(
          new RegExp(`\\{\\{${i + 1}\\}\\}`, 'g'),
          sendVars[name] || sample[name] || `{{${i + 1}}}`,
        ),
      body,
    );
  };

  const recipientCount =
    audience === 'all' ? (memberSummary?.withPhone ?? 0) : selectedMembers.length;

  const addMembers = (list: any[]) => {
    setSelectedMembers((prev) => {
      const seen = new Set(prev.map((m) => m._id));
      return [...prev, ...list.filter((m) => !seen.has(m._id))];
    });
  };

  const loadMemberSummary = useCallback(async () => {
    if (!studioId) return;
    try {
      const res: any = await apiService.getMessagingMembersSummary(studioId);
      setMemberSummary(res?.data ?? res);
    } catch {
      setMemberSummary(null);
    }
  }, [studioId]);

  useEffect(() => {
    if (tab === 1 && audience === 'all' && !memberSummary) loadMemberSummary();
  }, [tab, audience, memberSummary, loadMemberSummary]);

  const sendToMember = async () => {
    if (!recipientCount || !sendTemplate) return;
    if (
      recipientCount > 1 &&
      !window.confirm(
        `Send "${sendTemplate}" on WhatsApp to ${recipientCount} members? This can't be undone.`,
      )
    ) {
      return;
    }
    setSending(true);
    try {
      const res: any = await apiService.sendMessageToMember(
        audience === 'all'
          ? { allMembers: true, templateKey: sendTemplate, params: sendVars }
          : {
              userIds: selectedMembers.map((m) => m._id),
              templateKey: sendTemplate,
              params: sendVars,
            },
        studioId,
      );
      const data = res?.data ?? res;
      setToast({ msg: data?.message ?? 'Queued', sev: 'success' });
      setSelectedMembers([]);
      setTimeout(load, 4000);
    } catch (e: any) {
      setToast({ msg: errMsg(e, 'Send failed'), sev: 'error' });
    } finally {
      setSending(false);
    }
  };

  const runLookup = async () => {
    if (!lookupId.trim()) return;
    try {
      const [msgRes, consentRes] = await Promise.all([
        apiService.lookupMemberMessages(lookupId.trim(), studioId),
        apiService.getMemberConsent(lookupId.trim(), studioId).catch(() => null),
      ]);
      setLookupRows(((msgRes as any)?.data ?? msgRes)?.messages ?? []);
      setConsent(consentRes ? ((consentRes as any)?.data ?? consentRes) : null);
    } catch (e: any) {
      setToast({ msg: errMsg(e, 'Lookup failed'), sev: 'error' });
      setLookupRows([]);
      setConsent(null);
    }
  };

  const optOut = async () => {
    try {
      await apiService.optOutMember(lookupId.trim(), studioId);
      setToast({ msg: 'Member opted out of marketing', sev: 'success' });
      runLookup();
    } catch (e: any) {
      setToast({ msg: errMsg(e, 'Opt-out failed'), sev: 'error' });
    }
  };

  const locationPicker = mustPickLocation && (
    <Box mb={2} sx={{ maxWidth: 360 }}>
      <BranchSelect value={location} onChange={(v) => setLocation(v || '')} minRole="manager" />
    </Box>
  );

  if (mustPickLocation && !location) {
    return (
      <Box p={3}>
        <Typography variant="h5" mb={2}>Messaging</Typography>
        {locationPicker}
        <Alert severity="info" sx={{ mt: 2 }}>
          Pick a location — each location has its own messaging setup.
        </Alert>
      </Box>
    );
  }

  if (blocked && !embedded) {
    return (
      <Box p={3}>
        {locationPicker}
        <Alert severity="info">
          Messaging isn’t enabled for your studio yet. Contact SweatSocial support to turn it on.
        </Alert>
      </Box>
    );
  }
  const workspacePicker = !isStudioRole && (
    <Box mb={2}>
      {isSuperAdmin && (
        <ToggleButtonGroup
          size="small" exclusive value={workspace}
          onChange={(_, v) => v && setWorkspace(v)} sx={{ mb: 1.5 }}
        >
          <ToggleButton value="platform">SweatSocial app (our number)</ToggleButton>
          <ToggleButton value="studio">A studio’s number</ToggleButton>
        </ToggleButtonGroup>
      )}
      {isPlatform ? (
        <Alert severity="info">
          <strong>SweatSocial’s own WhatsApp.</strong> Only SweatSocial users ever
          hear from this number — their booking notifications, and anything you send here. Whitelabel
          app members and studio-created members never do; they hear from their studio’s number.
        </Alert>
      ) : (
        <StudioSelector value={studio} onChange={setStudio} />
      )}
    </Box>
  );

  if (!embedded && !isStudioRole && !studioId) {
    return (
      <Box p={3}>
        <Typography variant="h5" mb={2}>Messaging</Typography>
        {workspacePicker}
        <Alert severity="info" sx={{ mt: 2 }}>Select a studio to view its messaging setup.</Alert>
      </Box>
    );
  }

  const totals = overview?.byChannel ?? {};
  const sum = (ch: string, ...st: string[]) =>
    st.reduce((n, s) => n + (totals?.[ch]?.[s] ?? 0), 0);

  const Status = ({ ok, children }: { ok?: boolean; children: React.ReactNode }) => (
    <Stack direction="row" spacing={1} alignItems="flex-start" py={0.5}>
      {ok ? (
        <CheckCircle fontSize="small" color="success" sx={{ mt: '2px' }} />
      ) : (
        <ErrorOutline fontSize="small" color="warning" sx={{ mt: '2px' }} />
      )}
      <Typography variant="body2">{children}</Typography>
    </Stack>
  );

  return (
    <Box p={embedded ? 0 : 3}>
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        {!embedded && <Chat sx={{ color: colors?.primary }} />}
        {!embedded && <Typography variant="h5" flexGrow={1}>Messaging</Typography>}
        {embedded && <Box flexGrow={1} />}
        {canWrite && (
          <Button startIcon={<Science />} onClick={() => setTestOpen(true)} variant="outlined" size={embedded ? 'small' : 'medium'}>
            Send test
          </Button>
        )}
      </Stack>

      {!embedded && workspacePicker}
      {locationPicker}

      {loading && <CircularProgress size={24} sx={{ my: 2 }} />}

      {visibleTabs.length > 1 && (
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            mb: 2,
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
            ...(embedded
              ? { '& .Mui-selected': { color: '#128C7E !important' }, '& .MuiTabs-indicator': { bgcolor: '#128C7E' } }
              : {}),
          }}
        >
          {visibleTabs.map((i) => (
            <Tab key={i} value={i} label={TAB_LABELS[i]} />
          ))}
        </Tabs>
      )}

      {/* ── Overview ── */}
      {tab === 0 && (
        <Grid container spacing={2}>
          {['whatsapp', 'push', 'email'].map((ch) => (
            <Grid size={{ xs: 12, md: 4 }} key={ch}>
              <Card>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">{ch}</Typography>
                  <Typography variant="h4">{sum(ch, 'delivered', 'read', 'sent')}</Typography>
                  <Typography variant="body2" color="text.secondary">delivered / sent</Typography>
                  <Stack direction="row" spacing={1} mt={1}>
                    <Chip size="small" color="error" label={`${sum(ch, 'failed')} failed`} />
                    <Chip size="small" color="warning" label={`${sum(ch, 'suppressed', 'expired')} blocked`} />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
          <Grid size={{ xs: 12 }}>
            <Alert severity="info">
              Showing the last 7 days. “Blocked” means we chose not to send — no contact details,
              opted out, or outside messaging hours. Open the Delivery log for the reason.
            </Alert>
          </Grid>
        </Grid>
      )}

      {/* ── Send message ── */}
      {tab === 1 && (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" mb={1}>
                  {isPlatform ? 'Message SweatSocial users' : 'Message members'}
                </Typography>

                <TextField
                  select fullWidth size="small" margin="dense" label="Send to"
                  value={audience}
                  onChange={(e) => setAudience(e.target.value as any)}
                >
                  <MenuItem value="pick">{isPlatform ? 'Selected users' : 'Selected members'}</MenuItem>
                  <MenuItem value="all">
                    {isPlatform ? 'All SweatSocial app users' : 'All members'}
                    {memberSummary ? ` (${memberSummary.withPhone} with a phone number)` : ''}
                  </MenuItem>
                </TextField>

                {audience === 'all' ? (
                  <Alert severity={memberSummary && memberSummary.withPhone > memberSummary.max ? 'error' : 'info'} sx={{ mt: 1 }}>
                    {memberSummary
                      ? memberSummary.withPhone > memberSummary.max
                        ? `${memberSummary.withPhone} members have a phone number — one send can reach at most ${memberSummary.max}.`
                        : `Goes to ${memberSummary.withPhone} of ${memberSummary.total} members (the rest have no phone number). Members who opted out are skipped automatically.`
                      : 'Counting members…'}
                  </Alert>
                ) : (
                  <>
                    <TextField
                      fullWidth size="small" margin="dense" label={isPlatform ? 'Find SweatSocial users' : 'Find members'}
                      placeholder="Name, phone or email"
                      value={memberQuery} onChange={(e) => setMemberQuery(e.target.value)}
                    />

                    {memberResults.length > 0 && (
                      <Box mt={1} sx={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #eee', borderRadius: 1 }}>
                        <Box px={1.5} py={0.5} display="flex" justifyContent="space-between" alignItems="center" sx={{ bgcolor: 'action.hover' }}>
                          <Typography variant="caption" color="text.secondary">
                            {memberResults.length} result{memberResults.length === 1 ? '' : 's'} — click to add
                          </Typography>
                          <Button size="small" onClick={() => addMembers(memberResults.filter((m: any) => m.phoneNumber))}>
                            Add all
                          </Button>
                        </Box>
                        {memberResults.map((m: any) => {
                          const picked = selectedMembers.some((x) => x._id === m._id);
                          return (
                            <Box
                              key={m._id} px={1.5} py={1}
                              sx={{
                                cursor: m.phoneNumber && !picked ? 'pointer' : 'default',
                                opacity: m.phoneNumber ? 1 : 0.5,
                                '&:hover': { bgcolor: 'action.hover' },
                              }}
                              onClick={() => m.phoneNumber && !picked && addMembers([m])}
                            >
                              <Typography variant="body2" fontWeight={600}>
                                {m.name || 'Unnamed'} {picked && '✓'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {m.phoneNumber || 'no phone — cannot receive WhatsApp'}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    )}

                    {selectedMembers.length > 0 && (
                      <Box mt={1}>
                        <Stack direction="row" alignItems="center" mb={0.5}>
                          <Typography variant="body2" flexGrow={1}>
                            {selectedMembers.length} selected
                          </Typography>
                          <Button size="small" onClick={() => setSelectedMembers([])}>Clear</Button>
                        </Stack>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxHeight: 140, overflowY: 'auto' }}>
                          {selectedMembers.map((m) => (
                            <Chip
                              key={m._id} size="small"
                              label={m.name || m.phoneNumber}
                              onDelete={() => setSelectedMembers(selectedMembers.filter((x) => x._id !== m._id))}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </>
                )}

                {/* Unapproved templates are LISTED but disabled, with the reason
                    inline. An empty dropdown reads as broken; a disabled row with
                    "awaiting WhatsApp review" explains itself. */}
                <TextField
                  select fullWidth margin="normal" label="Template"
                  value={sendTemplate}
                  onChange={(e) => { setSendTemplate(e.target.value); setSendVars({}); }}
                  disabled={!recipientCount}
                >
                  {(templates?.templates ?? []).map((t: any) => {
                    const usable = t.status === 'approved';
                    return (
                      <MenuItem key={t.templateKey} value={t.templateKey} disabled={!usable}>
                        <Box display="flex" alignItems="center" gap={1} width="100%">
                          <span>{t.templateKey}</span>
                          {!usable && (
                            <Typography variant="caption" color="text.secondary">
                              — {TEMPLATE_STATUS_HINT[t.status] ?? t.status}
                            </Typography>
                          )}
                        </Box>
                      </MenuItem>
                    );
                  })}
                </TextField>

                {!(templates?.templates ?? []).some((t: any) => t.status === 'approved') && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    <strong>Nothing can be sent yet.</strong> WhatsApp only delivers
                    templates it has approved, and none of yours are approved.
                    Link and submit your templates on the Templates tab.
                  </Alert>
                )}

                {(activeTemplate?.variableOrder ?? []).map((v: string, i: number) => (
                  <TextField
                    key={v} fullWidth margin="dense" size="small"
                    label={`${v}  ({{${i + 1}}})`}
                    value={sendVars[v] ?? ''}
                    onChange={(e) => setSendVars({ ...sendVars, [v]: e.target.value })}
                    helperText={AUTO_VARS[v] ? `Leave blank to use ${AUTO_VARS[v]}` : undefined}
                  />
                ))}

                {canWrite && (
                  <Button
                    variant="contained" sx={{ mt: 2 }}
                    disabled={
                      !recipientCount || !sendTemplate || sending ||
                      (audience === 'all' && memberSummary && memberSummary.withPhone > memberSummary.max)
                    }
                    onClick={sendToMember}
                  >
                    {sending
                      ? 'Sending…'
                      : recipientCount > 1
                        ? `Send to ${recipientCount} members`
                        : 'Send'}
                  </Button>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" mb={2}>Preview</Typography>
                {previewText() ? (
                  <Box sx={{ p: 2, bgcolor: '#DCF8C6', borderRadius: 2, whiteSpace: 'pre-wrap' }}>
                    <Typography variant="body2">{previewText()}</Typography>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Pick a template to see how the message will look.
                  </Typography>
                )}
                <Divider sx={{ my: 2 }} />
                <Typography variant="caption" color="text.secondary">
                  Sends through the normal pipeline, so opt-outs and suppressions still
                  apply and the result appears in the Delivery Log.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* ── WhatsApp setup ── */}
      {tab === 2 && (
        <Grid container spacing={2}>
          {/* Connection status */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" mb={1}>
                  <Typography variant="h6" flexGrow={1}>Connection check</Typography>
                  <Button size="small" startIcon={checking ? <CircularProgress size={14} /> : <Refresh />} onClick={checkConnection} disabled={checking}>
                    Re-check
                  </Button>
                </Stack>
                {connection?.error && <Alert severity="error">{connection.error}</Alert>}
                {connection && !connection.error && (
                  <>
                    <Status ok={connection.credentials?.ok}>
                      <strong>Twilio account: </strong>
                      {connection.credentials?.ok
                        ? `connected — “${connection.credentials.friendlyName}”${
                            connection.credentials.source === 'platform'
                              ? isPlatform
                                ? ' (the server’s TWILIO_* account)'
                                : ' (SweatSocial’s shared account — connect your own below)'
                              : ''
                          }`
                        : connection.credentials?.humanReason ?? 'not connected'}
                    </Status>
                    <Status ok={connection.messagingService?.ok}>
                      <strong>WhatsApp sender: </strong>
                      {connection.messagingService?.ok
                        ? connection.messagingService.whatsappSenders?.length
                          ? `${connection.messagingService.friendlyName ?? 'Messaging Service'} → ${connection.messagingService.whatsappSenders.join(', ')}`
                          : connection.messagingService.humanReason
                        : connection.messagingService?.humanReason ?? 'connect the account first'}
                    </Status>
                    <Status ok={connection.enabled}>
                      <strong>WhatsApp: </strong>
                      {connection.killSwitch
                        ? 'paused by SweatSocial'
                        : connection.enabled
                          ? 'switched on'
                          : 'switched off — turn it on under Sender'}
                    </Status>
                    <Status ok={connection.webhook?.configured && !connection.webhook?.warning}>
                      <strong>Delivery receipts: </strong>
                      {!connection.webhook?.configured
                        ? connection.webhook?.humanReason
                        : connection.webhook?.warning ?? 'automatic (sent with every message)'}
                    </Status>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Step 1 — account */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6">
                  {isPlatform ? '1. SweatSocial’s Twilio account' : '1. Connect the studio’s Twilio account'}
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  {isPlatform
                    ? 'Uses the server’s TWILIO_* settings — nothing to enter. Only fill this in to use a different account.'
                    : 'The studio’s own account, from console.twilio.com → Account Info. Never SweatSocial’s account. Use the live credentials, not “Test credentials” (those can’t send WhatsApp).'}
                </Typography>
                {waConfig?.hasCredentials && (
                  <Alert
                    severity="success" sx={{ mb: 1 }}
                    action={
                      canWrite && (
                        <Button size="small" color="inherit" startIcon={<LinkOff />} onClick={disconnect}>
                          Disconnect
                        </Button>
                      )
                    }
                  >
                    Connected: account {waConfig.accountSid}. Enter new values below to replace them.
                  </Alert>
                )}
                <TextField
                  fullWidth margin="dense" label="Account SID" placeholder="AC…"
                  value={credForm.accountSid} disabled={!canWrite}
                  onChange={(e) => setCredForm({ ...credForm, accountSid: e.target.value })}
                />
                <TextField
                  fullWidth margin="dense" label="Auth Token" type="password"
                  value={credForm.authToken} disabled={!canWrite}
                  onChange={(e) => setCredForm({ ...credForm, authToken: e.target.value })}
                  helperText="Required — Twilio signs delivery receipts and replies with it."
                />
                <Button size="small" sx={{ mt: 1 }} onClick={() => setShowApiKey(!showApiKey)}>
                  {showApiKey ? 'Hide' : 'Use an API key instead (optional)'}
                </Button>
                <Collapse in={showApiKey}>
                  <TextField
                    fullWidth margin="dense" label="API Key" placeholder="SK…"
                    value={credForm.apiKey} disabled={!canWrite}
                    onChange={(e) => setCredForm({ ...credForm, apiKey: e.target.value })}
                  />
                  <TextField
                    fullWidth margin="dense" label="API Secret" type="password"
                    value={credForm.apiSecret} disabled={!canWrite}
                    onChange={(e) => setCredForm({ ...credForm, apiSecret: e.target.value })}
                    helperText="When set, messages are sent with the key; the Auth Token is still used for receipts."
                  />
                </Collapse>
                <Alert severity="info" sx={{ mt: 1 }}>
                  Checked with Twilio before saving, stored encrypted, never shown again.
                </Alert>
                {canWrite && (
                  <Button
                    variant="contained" onClick={saveCredentials} sx={{ mt: 2 }}
                    disabled={savingCreds || !credForm.accountSid.trim() || !credForm.authToken.trim()}
                  >
                    {savingCreds ? 'Checking with Twilio…' : waConfig?.hasCredentials ? 'Replace credentials' : 'Connect account'}
                  </Button>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Step 2 — sender */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6">2. WhatsApp sender</Typography>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  Twilio → Messaging → Services → your service (with your approved WhatsApp
                  number in its Sender Pool) → copy the Service SID.
                </Typography>
                <TextField
                  fullWidth margin="dense" label="Messaging Service SID"
                  placeholder="MG…" value={waForm.messagingServiceSid} disabled={!canWrite}
                  onChange={(e) => setWaForm({ ...waForm, messagingServiceSid: e.target.value.trim() })}
                  helperText="Recommended. Gives a sticky sender and automatic STOP handling."
                />
                <TextField
                  fullWidth margin="dense" label="Sender number (only if no Messaging Service)"
                  placeholder="+919876543210" value={waForm.fromNumber} disabled={!canWrite}
                  onChange={(e) => setWaForm({ ...waForm, fromNumber: e.target.value })}
                  helperText="Leave empty when a Messaging Service is set. Never the Twilio sandbox number."
                />
                <TextField
                  fullWidth margin="dense" label="Display name"
                  value={waForm.senderName} disabled={!canWrite}
                  onChange={(e) => setWaForm({ ...waForm, senderName: e.target.value })}
                />
                <TextField
                  fullWidth margin="dense" label="Quiet hours (marketing only)"
                  placeholder="22:00-08:00" value={waForm.quietHours} disabled={!canWrite}
                  onChange={(e) => setWaForm({ ...waForm, quietHours: e.target.value })}
                  helperText="Never delays class reminders or verification codes."
                />
                <FormControlLabel
                  sx={{ mt: 1 }}
                  control={
                    <Switch
                      checked={waForm.enabled} disabled={!canWrite}
                      onChange={(e) => setWaForm({ ...waForm, enabled: e.target.checked })}
                    />
                  }
                  label="WhatsApp enabled"
                />
                {canWrite && (
                  <Box>
                    <Button startIcon={<Save />} variant="contained" onClick={saveChannel} sx={{ mt: 1 }}>
                      Save sender
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Step 3 — inbound webhook */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6">3. Replies and STOP (recommended)</Typography>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  Delivery receipts need no setup. To receive member replies and honour STOP,
                  paste this URL in Twilio → Messaging → Services → your service → Integration →
                  “Send a webhook” (Incoming messages), method POST.
                </Typography>
                {connection?.webhook?.warning && (
                  <Alert severity="warning" sx={{ mb: 1 }}>{connection.webhook.warning}</Alert>
                )}
                {connection?.webhook?.url ? (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <TextField
                      fullWidth size="small" value={connection.webhook.url}
                      InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 13 } }}
                    />
                    <Tooltip title="Copy">
                      <IconButton onClick={() => copy(connection.webhook.url)}>
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                ) : (
                  <Alert severity="warning">
                    {connection?.webhook?.humanReason ?? 'Run the connection check to see the URL.'}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* ── Templates ── */}
      {tab === 3 && (
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <Box flexGrow={1}>
                {templates?.summary && (
                  <Alert severity={templates.summary.missing ? 'warning' : 'success'}>
                    {templates.summary.approved} of {templates.summary.total} WhatsApp templates approved.
                    {' '}Create a template here, or create it in Twilio’s Content Template Builder and link it.
                  </Alert>
                )}
              </Box>
              {canWrite && (
                <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
                  Create template
                </Button>
              )}
              {canWrite && (
                <Button
                  variant="outlined" startIcon={syncing ? <CircularProgress size={14} /> : <Sync />}
                  onClick={syncTemplates} disabled={syncing}
                >
                  Refresh status
                </Button>
              )}
            </Stack>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Template</TableCell>
                  <TableCell>Used for</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Twilio template</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(templates?.templates ?? []).map((t: any) => (
                  <TableRow key={t.templateKey}>
                    <TableCell>{t.templateKey}</TableCell>
                    <TableCell>
                      {(t.usedBy ?? []).map((u: any) => u.description).join(', ') || '—'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={
                          t.status === 'approved' ? 'success'
                            : t.status === 'rejected' ? 'error'
                            : t.status === 'submitted' ? 'info' : 'default'
                        }
                        label={t.status.replace('_', ' ')}
                      />
                      {t.rejectionReason && (
                        <Tooltip title={t.rejectionReason}>
                          <HelpOutline fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      {t.providerTemplateId ? (
                        <>
                          <div>{t.providerTemplateName ?? ''}</div>
                          <div style={{ fontFamily: 'monospace', color: '#888' }}>{t.providerTemplateId}</div>
                        </>
                      ) : '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {canWrite && (
                        <>
                          <Button size="small" onClick={() => openTemplateSetup(t)}>
                            {t.providerTemplateId ? 'Edit' : 'Link'}
                          </Button>
                          {t.providerTemplateId && t.status === 'draft' && (
                            <Button size="small" onClick={() => submitTemplate(t)}>
                              Submit
                            </Button>
                          )}
                          {t.status === 'rejected' && (
                            <Tooltip title="WhatsApp won't re-review rejected content. Create a corrected template in Twilio, then Edit to link it.">
                              <HelpOutline fontSize="small" sx={{ verticalAlign: 'middle', mx: 0.5 }} />
                            </Tooltip>
                          )}
                          {t._id && (
                            <Button size="small" color="error" onClick={() => removeTemplate(t)}>
                              Unlink
                            </Button>
                          )}
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* ── Delivery log ── */}
      {tab === 4 && (
        <Card>
          <CardContent>
            <Stack direction="row" justifyContent="flex-end" mb={1}>
              <Button size="small" startIcon={<Refresh />} onClick={load}>Refresh</Button>
            </Stack>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Member</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Channel</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {messages.map((m: any) => (
                  <TableRow key={m._id}>
                    <TableCell>{new Date(m.createdAt).toLocaleString('en-IN')}</TableCell>
                    <TableCell>{m.recipient?.id?.name ?? m.recipient?.kind}</TableCell>
                    <TableCell>{m.notificationType}</TableCell>
                    <TableCell>{m.channel}</TableCell>
                    <TableCell>
                      <Chip size="small" color={STATUS_COLOR[m.status] ?? 'default'} label={m.status} />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 320 }}>{m.failure?.humanReason ?? '—'}</TableCell>
                  </TableRow>
                ))}
                {!messages.length && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography color="text.secondary" py={2}>
                        No messages yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* ── Member lookup ── */}
      {tab === 5 && (
        <Card>
          <CardContent>
            <Typography variant="h6" mb={1}>Why didn’t my member get this?</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Enter a member’s phone number (or member ID) to see every message we tried to send them and what happened.
            </Typography>
            <Stack direction="row" spacing={1} mb={2}>
              <TextField
                size="small" fullWidth label="Phone number or member ID"
                placeholder="+91 98765 43210"
                value={lookupId} onChange={(e) => setLookupId(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') runLookup(); }}
              />
              <Button startIcon={<Search />} variant="contained" onClick={runLookup}>Look up</Button>
            </Stack>

            {consent && (
              <Alert
                severity={
                  (consent.consents ?? []).some((c: any) => c.state === 'withdrawn' || c.state === 'denied')
                    ? 'warning'
                    : 'info'
                }
                sx={{ mb: 2 }}
                action={
                  canWrite && (
                    <Button size="small" color="inherit" onClick={optOut}>
                      Opt out of marketing
                    </Button>
                  )
                }
              >
                {(consent.consents ?? []).length
                  ? `Marketing consent: ${(consent.consents ?? [])
                      .map((c: any) => `${c.channel} — ${c.state}`)
                      .join(', ')}`
                  : 'No marketing consent on record — marketing messages will be blocked.'}
              </Alert>
            )}
            {lookupRows && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>When</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Channel</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>What happened</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lookupRows.map((m: any) => (
                    <TableRow key={m._id}>
                      <TableCell>{new Date(m.createdAt).toLocaleString('en-IN')}</TableCell>
                      <TableCell>{m.notificationType}</TableCell>
                      <TableCell>{m.channel}</TableCell>
                      <TableCell>
                        <Chip size="small" color={STATUS_COLOR[m.status] ?? 'default'} label={m.status} />
                      </TableCell>
                      <TableCell>{m.failure?.humanReason ?? 'Delivered normally.'}</TableCell>
                    </TableRow>
                  ))}
                  {!lookupRows.length && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography color="text.secondary" py={2}>
                          Nothing was ever sent to this member.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Replies ── */}
      {tab === 6 && (
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" mb={1}>
              <Box flexGrow={1}>
                <Typography variant="h6">
                  {isPlatform ? 'Replies to SweatSocial’s number' : 'Replies from members'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  WhatsApp messages members sent to your number. You can answer in free text for
                  24 hours after their last message; after that, use an approved template.
                </Typography>
              </Box>
              <Button size="small" startIcon={<Refresh />} onClick={loadReplies}>Refresh</Button>
            </Stack>
            {connection?.webhook?.warning && (
              <Alert severity="warning" sx={{ mb: 1 }}>{connection.webhook.warning}</Alert>
            )}
            {replies === null ? (
              <CircularProgress size={24} sx={{ my: 2 }} />
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>When</TableCell>
                    <TableCell>From</TableCell>
                    <TableCell>Message</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {replies.map((r: any) => (
                    <TableRow key={r._id}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {new Date(r.createdAt).toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {r.member?.name ?? 'Unknown member'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{r.from}</Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 420, whiteSpace: 'pre-wrap' }}>
                        {r.body || r.buttonPayload || '—'}
                        {r.isOptOut && (
                          <Chip size="small" color="warning" label="opted out" sx={{ ml: 1 }} />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {canWrite && (
                          <Tooltip
                            title={
                              r.canReply
                                ? `Free-text replies allowed until ${new Date(r.windowClosesAt).toLocaleString('en-IN')}`
                                : 'More than 24h since their last message — use Send message with a template'
                            }
                          >
                            <span>
                              <Button
                                size="small" disabled={!r.canReply || r.isOptOut}
                                onClick={() => { setReplyTo(r); setReplyText(''); }}
                              >
                                Reply
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {!replies.length && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography color="text.secondary" py={2}>
                          No replies yet. Replies appear here once the webhook URL from WhatsApp setup
                          (step 3) is set in Twilio.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={!!replyTo} onClose={() => setReplyTo(null)} fullWidth maxWidth="sm">
        <DialogTitle>Reply to {replyTo?.member?.name ?? replyTo?.from}</DialogTitle>
        <DialogContent>
          <Box sx={{ p: 1.5, mb: 1, bgcolor: '#f1f1f1', borderRadius: 2, whiteSpace: 'pre-wrap' }}>
            <Typography variant="body2">{replyTo?.body}</Typography>
          </Box>
          <TextField
            fullWidth multiline minRows={3} label="Your reply" autoFocus
            value={replyText} onChange={(e) => setReplyText(e.target.value)}
            inputProps={{ maxLength: 1000 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReplyTo(null)}>Cancel</Button>
          <Button variant="contained" onClick={sendReply} disabled={replying || !replyText.trim()}>
            {replying ? 'Sending…' : 'Send reply'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Link a Twilio template ── */}
      <CreateTemplateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(msg) => { setToast({ msg, sev: 'success' }); load(); }}
        studioId={studioId}
        systemTemplates={templates?.templates ?? []}
      />

      <Dialog open={!!tplDialog} onClose={() => setTplDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle>Link “{tplDialog?.templateKey}”</DialogTitle>
        <DialogContent>
          {providerErr && <Alert severity="error" sx={{ mb: 1 }}>{providerErr}</Alert>}
          {providerTpls === null ? (
            <CircularProgress size={24} sx={{ my: 2 }} />
          ) : (
            <Stack direction="row" alignItems="center" spacing={1}>
            <TextField
              select fullWidth margin="dense" label="Twilio template"
              value={tplSid} onChange={(e) => setTplSid(e.target.value)}
              helperText={providerTpls.length ? 'Templates from your Twilio account.' : 'No templates found on your Twilio account yet.'}
            >
              {providerTpls.map((p: any) => (
                <MenuItem key={p.sid} value={p.sid}>
                  <Box>
                    <Typography variant="body2">
                      {p.friendlyName} · {p.language}
                      {p.whatsapp?.status ? ` · ${p.whatsapp.status}` : ' · not submitted'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(p.body ?? '').slice(0, 90)}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </TextField>
            <Tooltip title="Reload from Twilio">
              <IconButton onClick={loadProviderTemplates}>
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
            </Stack>
          )}

          {chosenProviderTpl && (
            <>
              <Box sx={{ p: 1.5, my: 1, bgcolor: '#DCF8C6', borderRadius: 2, whiteSpace: 'pre-wrap' }}>
                <Typography variant="body2">{chosenProviderTpl.body ?? '(no text body)'}</Typography>
              </Box>
              {tplVars.length > 0 && (
                <Typography variant="body2" color="text.secondary" mt={1}>
                  Which value goes into each placeholder? Use the field names SweatSocial fills in
                  automatically{tplDialog?.suggestedVariables?.length ? ` (${tplDialog.suggestedVariables.join(', ')})` : ''}.
                </Typography>
              )}
              {tplVars.map((v, i) => (
                <TextField
                  key={i} fullWidth margin="dense" size="small"
                  label={`{{${i + 1}}}`} value={v} disabled={mappingLocked}
                  onChange={(e) => setTplVars(tplVars.map((x, j) => (j === i ? e.target.value : x)))}
                />
              ))}
              {mappingLocked && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  This template is approved, so its variable mapping can’t change. Pick a new Twilio
                  template to change it.
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTplDialog(null)}>Cancel</Button>
          <Button
            variant="contained" onClick={saveTemplate}
            disabled={!tplSid || savingTpl || tplVars.some((v) => !v.trim())}
          >
            {savingTpl ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Test ── */}
      <Dialog open={testOpen} onClose={() => setTestOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Test WhatsApp</DialogTitle>
        <DialogContent>
          <TextField
            select fullWidth margin="dense" label="What to test"
            value={testMode} onChange={(e) => { setTestMode(e.target.value as any); setTestResult(null); }}
          >
            <MenuItem value="direct">Direct send — checks account, sender and template</MenuItem>
            <MenuItem value="pipeline">Full pipeline — lands in the Delivery Log</MenuItem>
          </TextField>

          {testMode === 'direct' ? (
            <>
              <TextField
                fullWidth margin="dense" label="To"
                placeholder="+91 98765 43210" value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
              />
              <TextField
                select fullWidth margin="dense" label="Template"
                value={testTemplate}
                onChange={(e) => { setTestTemplate(e.target.value); setTestVars({}); }}
                helperText="Free text only works within 24h of that number messaging your WhatsApp number. Otherwise use an approved template."
              >
                <MenuItem value="">Free text (no template)</MenuItem>
                {(templates?.templates ?? [])
                  .filter((t: any) => t.status === 'approved')
                  .map((t: any) => (
                    <MenuItem key={t.templateKey} value={t.templateKey}>{t.templateKey}</MenuItem>
                  ))}
              </TextField>
              {testTemplate
                ? (testTemplateRow?.variableOrder ?? []).map((v: string, i: number) => (
                    <TextField
                      key={v} fullWidth margin="dense" size="small"
                      label={`${v}  ({{${i + 1}}})`}
                      value={testVars[v] ?? ''}
                      onChange={(e) => setTestVars({ ...testVars, [v]: e.target.value })}
                    />
                  ))
                : (
                  <TextField
                    fullWidth margin="dense" label="Message (optional)" multiline minRows={2}
                    value={testBody} onChange={(e) => setTestBody(e.target.value)}
                  />
                )}
            </>
          ) : (
            <TextField
              fullWidth margin="dense" label="Member ID"
              value={testUserId} onChange={(e) => setTestUserId(e.target.value)}
              helperText="Sends booking_confirmed to this member through the full pipeline. Needs booking_confirmed approved."
            />
          )}

          {testResult && (
            <Alert severity={testResult.ok ? 'success' : 'error'} sx={{ mt: 2 }}>
              {testResult.detail}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestOpen(false)}>Close</Button>
          <Button variant="contained" onClick={runTestSend} disabled={testing}>
            {testing ? 'Sending… (checking delivery)' : 'Run test'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast?.sev ?? 'success'} onClose={() => setToast(null)}>
          {toast?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MessagingPage;
